import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAddWishlistItemMutation } from "../../wishlist/api/wishlistApi";
import { useRemoveSavedForLaterItemMutation } from "../api/savedForLaterApi";

/**
 * Moves an item from Saved For Later to Wishlist.
 * Requires fetching product data briefly to resolve Parent ID for variations.
 */
export const useMoveSavedForLaterItemToWishlist = () => {
  const { t } = useTranslation();
  const [addWishlistItem] = useAddWishlistItemMutation();
  const [removeSavedItem] = useRemoveSavedForLaterItemMutation();

  // We don't have the product object passed in directly in the list usually,
  // so we might need to rely on the fact that the component calling this
  // (PageSavedForLaterItem) has the 'item' prop with enriched data.
  // We will modify the hook signature to accept the item object or parentId.

  const [isMoving, setIsMoving] = useState(false);

  // Updated signature: accepts ID and optional ParentID.
  // If parentID isn't passed, we assume it's a simple product (parentId=0).
  const handleMoveSavedForLaterItemToWishlist = useCallback(
    async (id: number, parentId: number = 0) => {
      setIsMoving(true);
      const toastId = toast.loading(
        t("cartItemActions.moving", "Moving to wishlist...")
      );

      try {
        // Step 1: Add to Wishlist
        await addWishlistItem({ productId: id, parentId }).unwrap();

        // Step 2: Remove from Saved List
        await removeSavedItem(id).unwrap();

        toast.success(
          t("feedback.wishlist.addedSuccess", "Moved to wishlist!"),
          { id: toastId }
        );
      } catch (error) {
        console.error("Move to wishlist failed", error);
        toast.error(
          t("feedback.wishlist.toggleError", "Failed to move item."),
          { id: toastId }
        );
      } finally {
        setIsMoving(false);
      }
    },
    [addWishlistItem, removeSavedItem, t]
  );

  return { handleMoveSavedForLaterItemToWishlist, isMoving };
};
